import { useState } from 'react';
import SquarePaymentForm from './SquarePaymentForm';
import { logDebug } from '../../utils/logger';

const DISCLOSURE =
  "Online payments are charged at the standard listed rate. If you are eligible for a fee adjustment, please select 'Pay on the Day' and speak with an admin.";

interface PaymentChoiceProps {
  amountCents: number;
  amountDisplay: string;
  // #127 slice 5: FAMILY_MEMBER_ADD / FAMILY_CHANGE_REQUEST added for the
  // member "Add a family member" payment step (127-family-requests-contract.md).
  paymentType: 'MEMBERSHIP_NEW' | 'MEMBERSHIP_RENEWAL' | 'COMPETITION_ENTRY' | 'FAMILY_MEMBER_ADD';
  entityId: string;
  entityType: 'MEMBER' | 'COMPETITION_DAY_REGISTRATION' | 'EVENT_PRE_REGISTRATION' | 'FAMILY_CHANGE_REQUEST';
  /** Additive, optional (15 Aug 2026) — see SquarePaymentForm's doc comment. */
  entityIds?: string[];
  payerEmail: string;
  payerName: string;
  feeKey: string;
  authMode: 'apiKey' | 'userPool';
  resumePaymentToken?: string;
  onSuccess: (paymentId: string) => void;
  onPayOnDay: () => void;
  /** Backlog M10 — see SquarePaymentForm's doc comment. Optional and passed
   * straight through via {...formProps}; existing callers need no changes. */
  onReconciliationRequired?: (paymentId: string) => void;
}

const PaymentChoice: React.FC<PaymentChoiceProps> = (props) => {
  const { amountDisplay, onPayOnDay, onSuccess, ...formProps } = props;
  const [choice, setChoice] = useState<'online' | 'day' | null>(null);

  logDebug('PaymentChoice', 'render: choice=', choice, 'entityId=', props.entityId);

  if (choice === 'day') {
    onPayOnDay();
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Discount notice — always visible before a choice is made */}
      {choice === null && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          If you are eligible for a discount (e.g. committee member, volunteer, or for any other
          reason), please choose <strong>Pay on the Day</strong> and we'll sort it out on the day.
        </p>
      )}

      {choice === null && (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setChoice('online')}
            className="border-2 border-fishing-blue text-fishing-blue rounded-lg p-4 text-sm font-medium hover:bg-blue-50 transition-colors"
          >
            <div className="font-bold text-base mb-1">Pay Online Now</div>
            <div>{amountDisplay}</div>
          </button>
          <button
            onClick={() => setChoice('day')}
            className="border-2 border-gray-300 text-gray-700 rounded-lg p-4 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <div className="font-bold text-base mb-1">Pay on the Day</div>
            <div className="text-gray-500">Cash / EFTPOS</div>
          </button>
        </div>
      )}

      {choice === 'online' && (
        <>
          <SquarePaymentForm
            {...formProps}
            amountDisplay={amountDisplay}
            disclosureNotice={DISCLOSURE}
            onSuccess={onSuccess}
            onFailure={() => {}}
            onCancel={() => setChoice(null)}
          />
          <button onClick={() => setChoice(null)} className="text-sm text-gray-500 underline">
            ← Back
          </button>
        </>
      )}
    </div>
  );
};

export default PaymentChoice;
